import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Language, TranslationKey, translations } from './translations';

const STORAGE_KEY = 'bizforms-lang';

export type TranslateFn = (key: TranslationKey, params?: Record<string, string | number>) => string;

interface LanguageContextValue {
    lang: Language;
    setLang: (lang: Language) => void;
    t: TranslateFn;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const readStoredLang = (): Language => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return stored === 'en' ? 'en' : 'pl';
    } catch {
        return 'pl';
    }
};

export const translate = (lang: Language): TranslateFn => (key, params) => {
    const text = translations[lang][key] ?? translations.pl[key] ?? key;
    if (!params) return text;
    return Object.entries(params).reduce(
        (acc, [name, value]) => acc.replace(new RegExp(`{{${name}}}`, 'g'), String(value)),
        text,
    );
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
    const [lang, setLangState] = useState<Language>(readStoredLang);

    const setLang = useCallback((next: Language) => {
        setLangState(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // brak localStorage (tryb prywatny itp.) — wybór po prostu nie przetrwa odświeżenia
        }
    }, []);

    const value = useMemo<LanguageContextValue>(
        () => ({ lang, setLang, t: translate(lang) }),
        [lang, setLang],
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
    return ctx;
};
